<template>
  <div
    class="w-full flex justify-between grow gap-8 text-sm font-medium"
    :class="{
      'cursor-pointer': !disabled,
    }"
    @click.stop="handleClick(property.key)"
  >
    <div class="flex shrink-0 gap-2">
      <Icon :name="property.icon" size="md" class="mt-0.5 fg-quaternary" />
      <span class="text-tertiary">
        {{ property.label }}
      </span>
    </div>
    <div class="flex px-3 overflow-x-hidden">
      <template v-if="property.type === 'category'">
        <CategoryWithTooltip
          type="property"
          :category="property.value"
          :size="maxTablet ? 'md' : 'sm'"
        />
        <CategorySelectModal
          variant="create"
          :title="$t('components.task_create.modals.category_title')"
          :description="
            $t('components.task_create.modals.category_description')
          "
          :initialState="property.value"
          :buildingIds="buildingIds"
          :open="open"
          :teleport="false"
          @update="handleSubmit({ property: property.key, value: $event })"
        />
      </template>
      <template v-else-if="property.type === 'datepicker'">
        <span
          class="truncate"
          :class="{
            'text-placeholder_subtle': !property.value,
            'text-secondary': property.value,
          }"
        >
          {{ property.value ? formatDate(property.value) : placeholder }}
        </span>
        <DateSelectModal
          v-model:open="open"
          :title="$t('components.properties.purchased_at')"
          :initialState="property.value"
          @update="handleSubmit({ property: property.key, value: $event })"
        />
      </template>
      <template v-else>
        <TextPropertyDrawer
          v-if="maxSmall"
          :title="placeholder"
          :initialState="property.value"
          @submit="handleSubmit({ property: property.key, value: $event })"
          :open="isEditMode === property.key"
          @update:open="emit('update:isEditMode', '')"
          class="truncate"
          :class="{
            'text-placeholder_subtle': isEmpty(property.value),
            'text-primary': !isEmpty(property.value),
          }"
        >
          {{ !isEmpty(property.value) ? property.value : placeholder }}
        </TextPropertyDrawer>
        <EditableTextArea
          v-else
          ref="inputRef"
          :modelValue="property.value"
          @update:modelValue="
            handleSubmit({ property: property.key, value: $event })
          "
          :placeholder="placeholder"
          :readonly="disabled"
          activationMode="none"
          submitMode="both"
          class="text-right"
          :class="{
            'text-placeholder_subtle': isEmpty(property.value),
            'text-secondary': !isEmpty(property.value),
          }"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { isEmpty } from "lodash";
import { CategoryWithTooltip } from "@/SharedComponents/CategoryWithTooltip";
import { Icon } from "@/SharedComponents/Icon";
import { EditableTextArea } from "@/SharedComponents/Inputs";
import { TextPropertyDrawer } from "@/Pages/Assets/Partials/Create";
import { useDate } from "@/Composables/useDate";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import {
  CategorySelectModal,
  DateSelectModal,
} from "@/SharedComponents/Modals";

// Breakpoints
const { maxSmall, maxTablet } = useBreakpoints();
import { useI18n } from "vue-i18n";

// Date utility
const { formatDate } = useDate();

// Emit
const emit = defineEmits(["update:form", "goToStep", "update:isEditMode"]);

// Refs
const inputRef = ref(undefined);
const open = ref(false);

// Define Props
const props = defineProps({
  property: { type: Object, required: true },
  isEditMode: { type: String, required: false },
  size: { type: String, default: "md" },
  buildingId: { type: String, required: false },
  disabled: { type: Boolean, required: false },
});

const { t } = useI18n();

const placeholder = computed(() => {
  const property = t("components.properties." + props.property.key);
  if (props.disabled) {
    return t("components.details.properties.no", { property });
  }
  return t("components.details.properties.set", { property });
});

const buildingIds = computed(() =>
  props.buildingId ? [props.buildingId] : []
);

const handleClick = (key) => {
  if (props.disabled) {
    return;
  }

  if (key === "category" || key === "purchased_at") {
    if (!maxSmall.value) {
      emit("goToStep", key);
    } else {
      open.value = true;
    }
  } else {
    if (props.isEditMode !== key) {
      emit("update:isEditMode", key);
      inputRef.value?.edit();
    } else {
      emit("update:isEditMode", "");
    }
  }
};

const handleSubmit = (data) => {
  const { property, value } = data;
  emit("update:form", { property, value });
  emit("update:isEditMode", "");
  open.value = false;
};
</script>
